import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TenantUserRoles } from '@buspro/shared/common';
import { SettingsApi } from '@buspro/web/apis';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NgxPermissionsModule } from 'ngx-permissions';
import { lastValueFrom } from 'rxjs';

/**
 * Cash handover rules (docs/CASH_ACCOUNTING_PLAN.md, decision 3).
 *
 * How long after a trip ENDS a person has to hand its cash in before it goes
 * short. Counted from the end of each trip, never a clock cut-off, so a trip
 * that runs past midnight is never split. Admins, the Director and the
 * Accountant may change it; everyone else reads it.
 */
@Component({
  selector: 'tenant-cash-settings-section',
  standalone: true,
  imports: [CommonModule, FormsModule, NzButtonModule, NzInputNumberModule, NzSkeletonModule, NgxPermissionsModule],
  providers: [NzNotificationService],
  template: `
    <div class="settings-section">
      <div class="settings-section-header">
        <h2 class="settings-section-title">Cash handovers</h2>
        <p class="settings-section-description">
          How long after a trip ends a conductor or clerk has to hand in its cash before it is recorded short.
        </p>
      </div>
      <div class="settings-section-content space-y-3">
        @if (settingsQuery.isPending()) {
          <nz-skeleton [nzActive]="true" [nzParagraph]="{ rows: 2 }"></nz-skeleton>
        } @else {
          <label class="block text-sm font-medium text-base-content" for="cash-grace-hours">
            Grace period after a trip ends (hours)
          </label>
          <div class="flex items-center gap-3">
            <nz-input-number
              id="cash-grace-hours"
              [nzMin]="0"
              [nzMax]="720"
              [nzStep]="1"
              [ngModel]="hours()"
              (ngModelChange)="edited.set($event)"
              [nzDisabled]="!canEdit()"
            ></nz-input-number>
            <ng-container *ngxPermissionsOnly="editors">
              <button nz-button nzType="primary" [nzLoading]="saving()" (click)="save()">Save</button>
            </ng-container>
          </div>
          <p class="text-sm text-base-content/60">
            A running trip never goes short. Money handed in after the grace period still clears the shortfall and
            cancels a deduction not yet applied. 24 hours is the default; 0 means short as soon as the trip ends.
          </p>
        }
      </div>
    </div>
  `,
  styles: [
    `
      .settings-section {
        display: flex;
        flex-direction: column;
        gap: 24px;
      }

      .settings-section-header {
        border-bottom: 1px solid var(--sidebar-border);
        padding-bottom: 16px;
      }

      .settings-section-title {
        font-size: 20px;
        font-weight: 600;
        color: var(--header-text);
        margin: 0 0 4px 0;
      }

      .settings-section-description {
        margin: 0;
        opacity: 0.7;
      }
    `,
  ],
})
export class CashSettingsSectionComponent {
  private readonly settingsApi = inject(SettingsApi);
  private readonly notification = inject(NzNotificationService);

  /**
   * Who may change the cash settings.
   *
   * The Director was dropped with the rest of the cash area (see
   * `cash-shared.ts`, 2026-09-16): no part of cash is theirs, and the grace
   * period decides when a person's money is called short.
   */
  readonly editors = [TenantUserRoles.TENANT_ADMIN, TenantUserRoles.TENANT_ACCOUNTANT];

  readonly settingsQuery = injectQuery(() => ({
    queryKey: ['tenant-settings'],
    queryFn: () => lastValueFrom(this.settingsApi.tenantSettingsControllerGetTenantSettings()),
    staleTime: 5 * 60 * 1000,
  }));

  /** What was typed; null until changed, so the saved value shows. No effect writes a signal (NG0600). */
  readonly edited = signal<number | null>(null);
  readonly hours = computed(() => this.edited() ?? this.settingsQuery.data()?.cashShortfallGraceHours ?? null);
  readonly saving = signal(false);
  readonly canEdit = signal(true);

  async save(): Promise<void> {
    const hours = this.hours();
    if (hours === null || hours < 0 || hours > 720 || !Number.isInteger(hours)) {
      this.notification.error('Not saved', 'Enter whole hours between 0 and 720.');
      return;
    }
    this.saving.set(true);
    try {
      await lastValueFrom(
        this.settingsApi.tenantSettingsControllerUpdateCashSettings({ body: { cashShortfallGraceHours: hours } }),
      );
      this.notification.success('Saved', `Cash goes short ${hours} hours after a trip ends.`);
      await this.settingsQuery.refetch();
      this.edited.set(null);
    } catch (error) {
      const message = (error as { error?: { message?: string | string[] } })?.error?.message;
      this.notification.error('Not saved', Array.isArray(message) ? message.join(' ') : (message ?? 'Try again.'));
    } finally {
      this.saving.set(false);
    }
  }
}
